import {ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit, ViewChild} from "@angular/core";
import {FormBuilder, FormControl, FormGroup} from "@angular/forms";
import {AutocompleteComponent, AutocompleteLoadingModeType, ToastService} from "maps-angular-components";
import {BehaviorSubject, Subject, Subscription} from "rxjs";
import {GrupoCarteira} from "../grupo-carteira";
import {GrupoCarteiraService} from "../grupo-carteira.service";
import {indicate} from "../../shared/util/loading-indicate";
import {ResultPage} from "./result-page";
import {MatDialog} from "@angular/material/dialog";
import {ModalEditarGrupoCarteiraComponent} from "./modal-editar-grupo-carteira/modal-editar-grupo-carteira.component";
import {
  ModalDeletarGrupoCarteiraComponent
} from "./modal-deletar-grupo-carteira/modal-deletar-grupo-carteira.component";
import {BrowserSupport} from "../../shared/util/browser-support";

interface IAutocompleteSchema<T> {
  service: any;
  endpoint: string;
  label: string;
  loadingMode: AutocompleteLoadingModeType;
  autoload: boolean;
  formGroup: FormGroup;
  formControlName: string;
  panelClass: string;

  parseSearchParam(search: string): string;
}

@Component({
  selector: "gerenciador-registros",
  templateUrl: "./gerenciador-registros.component.html",
  styleUrls: [
    "./gerenciador-registros.component.scss",
    "../../shared/form-component.css",
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GerenciadorRegistrosComponent implements OnInit, OnDestroy {
  private readonly subscriptions = new Subscription();

  @ViewChild("grupocarteiraAutocomplete", { static: false })
  private readonly grupocarteiraAutocomplete: AutocompleteComponent<GrupoCarteira>;

  gruposcarteiras: GrupoCarteira[] = [];
  grupocarteiraSchema: IAutocompleteSchema<GrupoCarteira>;
  totalRegistros = 0;
  paginationTotalPage = 0;
  page$: Subject<number> = new Subject<number>();
  loading$ = new BehaviorSubject(false);
  loadingExport$: Subject<boolean> = new Subject<boolean>();
  showExecutar = false;
  private pageSize = 20;

  @Input() formConfig: any = {};

  form: FormGroup;

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly service: GrupoCarteiraService,
    private readonly messageService: ToastService,
    private readonly dialog: MatDialog
  ) {
  }

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      id: new FormControl(),
      page: new FormControl(0),
    });

    this.page$.subscribe((value) =>
      this.form.controls["page"].setValue(value - 1)
    );

    this.grupocarteiraSchema = {
      service: this.service,
      endpoint: "",
      label: "Grupo carteira",
      loadingMode: AutocompleteLoadingModeType.LAZY,
      autoload: false,
      formGroup: this.form,
      formControlName: "id",
      panelClass: "w-100",
      parseSearchParam(search): string {
        return search;
      },
    };
  }

  limparForm() {
    this.form.reset();
    this.markAsPending();
  }

  markAsPending(): void {
    Object.keys(this.form.controls).forEach((field) => {
      const control = this.form.get(field);
      if (control instanceof FormControl) {
        control.markAsPending();
      }
    });
  }

  receiverPage(value: any) {
    this.page$.next(value);
    this.pesquisar(this.form);
  }

  openEditDialog(grupoCarteira: GrupoCarteira) {
    let dialogRef = this.dialog.open(ModalEditarGrupoCarteiraComponent, {
      data: grupoCarteira,
      minWidth: "55vw",
      maxWidth: "55vw",
      maxHeight: "99vh",
      autoFocus: false,
    });

    dialogRef.componentInstance.editado.subscribe(() => {
      this.pesquisar(this.form)
      dialogRef.close()
    });
  }

  openDeleteDialog(id: string) {
    let dialogRef = this.dialog.open(ModalDeletarGrupoCarteiraComponent, {
      data: id,
      width: "800px"
    });

    dialogRef.componentInstance.grupoExcluido.subscribe(() => {
      this.pesquisar(this.form)
      dialogRef.close()
    });
  }

  pesquisar(form: FormGroup): void {
    this.gruposcarteiras = [];
    this.service
      .search(form.value, this.pageSize)
      .pipe(indicate(this.loading$))
      .subscribe(
        (res: ResultPage) => {
          this.showExecutar = true;
          if (res.metadata.totalElements > 0) {
            if (res.metadata.size === 0) {
              this.paginationTotalPage = 1;
            } else {
              this.paginationTotalPage = Math.ceil(
                res.metadata.totalElements / res.metadata.size
              );
            }
            this.totalRegistros = res.metadata.totalElements;
            this.gruposcarteiras = res.content.map((e) =>
              Object.assign(new GrupoCarteira(), e)
            );
            this.form.get("page").setValue(res.metadata.number);
          } else {
            this.paginationTotalPage = 1;
          }
        },
        (err) => this.messageService.erro(err.error.message)
      );
  }

  onChangeTotalPagina(e) {
    this.pageSize = e;
    this.page$.next(1);
    this.pesquisar(this.form);
  }

  exportarPdf() {
    this.subscriptions.add(
      this.service.exportPdf(this.form.value).pipe(indicate(this.loadingExport$)).subscribe(data => {
        BrowserSupport.savePDF(data);
      }, err => this.messageService.erro(err.error.message)));
  }

  exportarXls() {
    this.subscriptions.add(
      this.service.exportXlsx(this.form.value).pipe(indicate(this.loadingExport$)).subscribe(data => {
        BrowserSupport.saveXlsx(data);
      }, err => this.messageService.erro(err.error.message)));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
